import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MyPriceAlerts, PriceAlert } from '@contracts/priceAlerts';
import { api, ApiError } from '../lib/api';
import { dateTime, price } from '../lib/format';
import { useLive } from '../store/live';
import { Panel } from './Panel';

const KEY = ['price-alerts'];

function AlertRow({
  a,
  bid,
  onCancel,
  busy,
}: {
  a: PriceAlert;
  bid: number | null;
  onCancel: () => void;
  busy: boolean;
}) {
  const up = a.direction === 'above';
  const dist = bid === null ? null : a.price - bid;
  return (
    <li className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
      <span>
        <span className={up ? 'pos' : 'neg'}>
          <span aria-hidden="true">{up ? '▲' : '▼'}</span> {up ? 'ขึ้นถึง' : 'ลงถึง'}
        </span>{' '}
        <strong data-testid="alert-price">{price(a.price)}</strong>
        {a.note ? <span className="small muted"> — {a.note}</span> : null}
        {dist !== null ? (
          <span className="small muted">
            {' '}
            · ห่าง {dist > 0 ? '+' : ''}
            {dist.toFixed(2)}
          </span>
        ) : null}
      </span>
      <button
        type="button"
        className="btn btn--danger"
        onClick={onCancel}
        disabled={busy}
        aria-label={`ยกเลิก ${price(a.price)}`}
      >
        ยกเลิก
      </button>
    </li>
  );
}

/** The signed-in user's own one-time price alerts: fires once when the bid reaches the price, then is done. */
export function PriceAlertsPanel() {
  const qc = useQueryClient();
  const liveBid = useLive((s) => s.quote?.bid ?? null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const mine = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<MyPriceAlerts>('/api/v1/price-alerts'),
    refetchInterval: 10_000,
  });
  const create = useMutation({
    mutationFn: (body: { price: number; note: string }) =>
      api.post<{ alert: PriceAlert }>('/api/v1/price-alerts', body),
    onSuccess: () => {
      setValue('');
      setNote('');
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/price-alerts/${id}/cancel`),
    onSettled: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  const d = mine.data;
  const bid = liveBid ?? d?.bid ?? null;
  const target = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(target) && target > 0;

  const toggle = () => {
    if (!open && value === '' && bid !== null) setValue(bid.toFixed(2));
    setOpen(!open);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) create.mutate({ price: target, note: note.trim() });
  };

  return (
    <Panel
      title="🔔 แจ้งเตือนราคาของฉัน"
      id="price-alerts"
      meta={
        <button type="button" className="btn btn--primary" onClick={toggle} aria-expanded={open}>
          {open ? 'ปิด' : '＋ ตั้งแจ้งเตือนราคา'}
        </button>
      }
    >
      {open ? (
        <form className="form-grid" onSubmit={submit} style={{ marginBottom: 12 }}>
          <label className="field">
            ราคา (Bid)
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          </label>
          <label className="field">
            โน้ต (ไม่บังคับ)
            <input type="text" maxLength={120} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button type="submit" className="btn btn--primary" disabled={!valid || create.isPending}>
            {create.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
          {valid && bid !== null && Math.abs(target - bid) >= 0.01 ? (
            <p className="small muted" style={{ margin: 0, gridColumn: '1 / -1' }}>
              จะแจ้งเมื่อราคา{target > bid ? 'ขึ้น' : 'ลง'}ถึง {price(target)} (ตอนนี้ {price(bid)}) —
              แจ้งครั้งเดียวแล้วจบ
            </p>
          ) : null}
          {create.error ? (
            <p className="small neg" role="alert" style={{ margin: 0, gridColumn: '1 / -1' }}>
              {create.error instanceof ApiError ? create.error.message : 'บันทึกไม่สำเร็จ'}
            </p>
          ) : null}
        </form>
      ) : null}

      {mine.isLoading ? <p className="small muted">กำลังโหลด…</p> : null}
      {mine.isError ? <p className="small neg">โหลดรายการแจ้งเตือนไม่ได้</p> : null}
      {d ? (
        <>
          {d.active.length === 0 ? (
            <p className="small muted" style={{ margin: 0 }}>
              ยังไม่มีการแจ้งเตือนที่รออยู่ — กด “ตั้งแจ้งเตือนราคา” เพื่อเพิ่ม (สูงสุด {d.maxActive} รายการ)
            </p>
          ) : (
            <ul className="list" aria-label="แจ้งเตือนที่รออยู่">
              {d.active.map((a) => (
                <AlertRow key={a.id} a={a} bid={bid} busy={cancel.isPending} onCancel={() => cancel.mutate(a.id)} />
              ))}
            </ul>
          )}
          {d.triggered.length > 0 ? (
            <details style={{ marginTop: 8 }}>
              <summary className="small">แจ้งไปแล้ว ({d.triggered.length})</summary>
              <ul className="list">
                {d.triggered.map((a) => (
                  <li key={a.id} className="small">
                    <span aria-hidden="true">✓</span> {a.direction === 'above' ? 'ขึ้นถึง' : 'ลงถึง'} {price(a.price)}
                    {a.note ? ` — ${a.note}` : ''}
                    <span className="muted">
                      {' '}
                      · Bid {a.triggerBid !== null ? price(a.triggerBid) : '—'}
                      {a.triggeredAt ? ` · ${dateTime(a.triggeredAt)}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <p className="small muted" style={{ margin: '8px 0 0' }}>
            แจ้งครั้งเดียวแล้วจบ · ส่งเข้าแอป (Web Push) และ LINE ถ้าเปิด “Send to my LINE” ไว้
          </p>
        </>
      ) : null}
    </Panel>
  );
}
